import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Tooltip, Zoom, Dialog, Button, Typography, Box, Avatar } from "@mui/material";
import { 
  IoHomeOutline, IoSparklesOutline, IoAdd, 
  IoFolderOpenOutline, IoLogOutOutline, 
  IoChevronBackOutline, IoChevronForwardOutline,
  IoSchoolOutline, 
  IoBriefcaseOutline,
  IoPeopleOutline,
  IoChatbubbleEllipsesOutline,
  IoNewspaperOutline,
  IoTimeOutline,
  IoCloseCircle
} from "react-icons/io5";
import { MdOutlineUpdate, MdOutlineAssignment } from "react-icons/md";
import LogoutModal from "../logoutModal/logoutModal"; 
import ProPlanModal from "../../../components/pro/ProPlanModal";
import ProPlansInfoModal from "../../../components/pro/ProPlansInfoModal";
import CreateNewModal from "./CreateNewModal";
import { useSystems } from "../../../../context/SystemContext";
import { useAuth } from "../../../../context/AuthContext.jsx";

const TRIALS_API = "https://apiweb.hitpoly.com/ajax/trialsController.php";

const COLORS = {
  white: "#FFFFFF",
  dark: "#211E26",
  fucsia: "#F21C63",
  fucsiaBajo: "#a18bfaff",
  purple: "#6C4DE2",
  hover: "#f4f4f4",
  active: "#ECEAEF",
};

const COLLAPSED_WIDTH = 75;
const EXPANDED_WIDTH = 250;
const TOP_OFFSET = 60;

// NavItem extraído FUERA del componente padre para que React no lo desmonte/remonte
// en cada re-render del sidebar (lo cual causaba que clicks se perdieran).
const NavItem = React.memo(({ name, icon, route, isCollapsed, currentPathname, onNavigate, isPro, onProClick, proActivo, proOrigen, proFin, profileReady }) => {
  const [hover, setHover] = useState(false);
  
  const cleanRoute = route.split('?')[0];
  const isActive = currentPathname === cleanRoute || 
                   (cleanRoute !== '/' && currentPathname.startsWith(cleanRoute)) ||
                   (cleanRoute === '/dashboardsistem' && currentPathname === '/');
  
  // Si está en prueba gratis, mostramos los días que le quedan en vez de "PRO".
  const enPrueba = proActivo && proOrigen === "prueba" && proFin;
  let badgeText = "PRO";
  if (enPrueba) {
    const ms = new Date(proFin).getTime() - Date.now();
    const dias = Math.max(0, Math.ceil(ms / 86400000));
    badgeText = `${dias}d`;
  }
  
  const handleClick = (e) => {
    e.stopPropagation();
    // Limpiar siempre la ruta guardada del sistema destino
    const match = route.match(/\/systems\/([^?]+)/);
    if (match) {
      const sysId = match[1];
      localStorage.removeItem('last_path_' + sysId);
    }
    if (isPro) {
      // Perfil principal (sin perfil profesional/empresa): siempre pasar por
      // el gate, aunque el trial esté activo.
      if (!profileReady) {
        if (onProClick) {
          onProClick({ name, route });
        }
        return;
      }
      // Si ya tiene la prueba gratis activa, va directo al proyecto sin abrir popup
      if (proActivo) {
        onNavigate(route);
        return;
      }
      if (onProClick) {
        onProClick({ name, route });
      } else {
      }
      return;
    }
    onNavigate(route);
  };

  const content = (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={handleClick}
      style={{
        display: "flex",
        alignItems: "center",
        height: "48px",
        padding: isCollapsed ? "0" : "0 20px",
        cursor: "pointer",
        justifyContent: isCollapsed ? "center" : "flex-start",
        backgroundColor: isActive ? COLORS.active : hover ? COLORS.hover : "transparent",
        borderRight: isActive ? `3px solid ${COLORS.fucsia}` : "3px solid transparent",
        transition: "0.2s ease",
        marginBottom: "4px",
        position: "relative",
        zIndex: 2,
      }}
    >
      <div style={{ 
        display: "flex", 
        marginRight: isCollapsed ? "0" : "12px", 
        color: isActive ? COLORS.fucsia : COLORS.dark,
        transition: "color 0.2s"
      }}>
        {React.cloneElement(icon, { size: 22 })}
      </div>
      {!isCollapsed && (
        <span style={{ 
          fontSize: "14px", 
          fontWeight: isActive ? 700 : 500,
          color: COLORS.dark, 
          whiteSpace: "nowrap" 
        }}>
          {name.toUpperCase()}
        </span>
      )}
      {!isCollapsed && isPro && (
        <span style={{
          marginLeft: "8px",
          fontSize: "9px",
          fontWeight: 800,
          letterSpacing: "0.5px",
          color: "#fff",
          backgroundColor: enPrueba ? COLORS.purple : COLORS.fucsia,
          borderRadius: "6px",
          padding: "2px 6px",
          display: "inline-flex",
          alignItems: "center",
          gap: "3px",
        }}>{enPrueba && <IoTimeOutline size={10} />}{badgeText}</span>
      )}
      {isCollapsed && isPro && (
        <span style={{
          position: "absolute",
          top: "6px",
          right: "10px",
          fontSize: "8px",
          fontWeight: 800,
          color: "#fff",
          backgroundColor: enPrueba ? COLORS.purple : COLORS.fucsia,
          borderRadius: "5px",
          padding: "1px 4px",
          lineHeight: 1,
          display: "inline-flex",
          alignItems: "center",
          gap: "2px",
        }}>{enPrueba && <IoTimeOutline size={9} />}{badgeText}</span>
      )}
    </div>
  );

  return isCollapsed ? (
    <Tooltip title={name.toUpperCase()} placement="right" TransitionComponent={Zoom} arrow>
      {content}
    </Tooltip>
  ) : content;
});

const CollapsibleSidebar = ({ isMobile: isMobileProp, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation(); // Hook para obtener la ruta actual
  const { isSidebarCollapsed, setIsSidebarCollapsed, activeProfile, setActiveProfile } = useSystems();
  const [internalIsMobile, setInternalIsMobile] = useState(window.innerWidth < 768);
  const isManualCollapsed = isSidebarCollapsed;
  const setIsManualCollapsed = setIsSidebarCollapsed;
  const [isHovered, setIsHovered] = useState(false);
  const [isCreateHovered, setIsCreateHovered] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isLogoutHovered, setIsLogoutHovered] = useState(false);
  const [isProModalOpen, setIsProModalOpen] = useState(false);
  const [proTool, setProTool] = useState("");
  const [proRoute, setProRoute] = useState("");
  const [showSubscriptionSuccess, setShowSubscriptionSuccess] = useState(false);
  const [trialVencido, setTrialVencido] = useState(false);
  const [isProInfoOpen, setIsProInfoOpen] = useState(false);
  const [proInfoTool, setProInfoTool] = useState(null);
  const [proActivo, setProActivo] = useState(false);
  const [proOrigen, setProOrigen] = useState(null);
  const [proFin, setProFin] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreateProfileDialogOpen, setIsCreateProfileDialogOpen] = useState(false);
  const [proCreateRoute, setProCreateRoute] = useState("");
  const [profileChooserOpen, setProfileChooserOpen] = useState(false);
  const [chooserProfiles, setChooserProfiles] = useState([]);
  const [chooserRoute, setChooserRoute] = useState("");
  const [chooserTool, setChooserTool] = useState("");
  const { user: authUser } = useAuth();

  // Solo un perfil profesional o empresa puede entrar a los sistemas Pro.
  const profileReady = activeProfile?.type === "professional" || activeProfile?.type === "enterprise";

  // Popup Pro (de colores) con la lógica que ya tenemos — solo este debe salir
  const openProInfoModal = useCallback((tool) => {
    const toolStr = (tool && typeof tool === "object" && tool.name) ? tool.name : (tool || "");
    const toolRoute = (tool && typeof tool === "object" && tool.route) ? tool.route : "";

    // Gate de tipo de perfil: crm / bolsa requieren perfil profesional o empresa.
    const isProSystemRoute = /\/systems\/(crm|bolsa)/.test(toolRoute || "");
    if (isProSystemRoute && activeProfile?.type !== "professional" && activeProfile?.type !== "enterprise") {
      let profiles = [];
      try {
        profiles = JSON.parse(localStorage.getItem("cached_profiles") || "[]") || [];
      } catch (e) {}
      const candidates = profiles.filter((p) => p.type === "professional" || p.type === "enterprise");
      if (candidates.length >= 2) {
        // Tiene profesional Y empresa: preguntar con cuál entrar
        setChooserProfiles(candidates);
        setChooserRoute(toolRoute);
        setChooserTool(toolStr);
        setProfileChooserOpen(true);
        return;
      }
      if (candidates.length === 1) {
        // Solo un perfil pro/empresa: activarlo y continuar (trial o destino)
        setActiveProfile(candidates[0]);
        if (proActivo) {
          navigate(toolRoute);
        } else {
          setProTool(toolStr);
          setProRoute(toolRoute);
          setIsProModalOpen(true);
        }
        return;
      }
      // Sin perfil pro/empresa: pedir crear uno y reanudar el destino después
      setProTool(toolStr);
      setProRoute(toolRoute);
      setProCreateRoute(toolRoute);
      setIsCreateProfileDialogOpen(true);
      return;
    }

    setProTool(toolStr);
    setProRoute(toolRoute);
    setIsProModalOpen(true);
  }, [activeProfile, proActivo, navigate, setActiveProfile]);

  // Guarda el destino pendiente + tipo de creación y entra a la bolsa.
  const iniciarCreacionPerfil = (tipo) => {
    try {
      let profiles = [];
      try {
        profiles = JSON.parse(localStorage.getItem("cached_profiles") || "[]") || [];
      } catch (e) {}
      const before = profiles.map((p) => ({ id: p.id, type: p.type }));
      localStorage.setItem("pending_pro_destination", JSON.stringify({ route: proCreateRoute, before, t: Date.now() }));
    } catch (e) {}
    sessionStorage.setItem("bolsa_crear_tipo", tipo);
    setIsCreateProfileDialogOpen(false);
    navigate("/systems/bolsa?reset=true");
    if (onClose) onClose();
  };

  // Selección del popup: entrar al sistema Pro con el perfil elegido
  const seleccionarPerfilDeEntrada = (p) => {
    setProfileChooserOpen(false);
    setActiveProfile({ id: p.id, type: p.type, name: p.name, label: p.label, avatar: p.avatar });
    if (proActivo) {
      navigate(chooserRoute);
    } else {
      setProTool(chooserTool);
      setProRoute(chooserRoute);
      setIsProModalOpen(true);
    }
  };

  const openProModal = useCallback((tool) => {
    setProTool(tool || "");
    setIsProModalOpen(true);
  }, []);

  // La academia (iframe) pide abrir el popup Pro para suscribirse/prueba.
  useEffect(() => {
    const onMessage = (e) => {
      const d = e.data;
      if (d && d.type === "OPEN_PRO_MODAL") {
        openProInfoModal();
      }
      // Redirección tras registro en la bolsa
      if (d && d.type === "REGISTRATION_COMPLETE") {
        const currentUserId = authUser?.id || authUser?.user_id || 1500;
        const profileType = d.profileType || d.tipo || d.userType || "profesional";
        if (profileType === "empresa" || profileType === "enterprise") {
          navigate(`/systems/bolsa/profesionales?userId=${currentUserId}`);
        } else {
          navigate(`/systems/bolsa/vacantes?userId=${currentUserId}`);
        }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [openProInfoModal, navigate, authUser]);

  // Consulta si el usuario tiene Pro activo (prueba gratis vigente o ya pagado).
  useEffect(() => {
    const applyLocal = () => {
      try {
        const raw = localStorage.getItem("hitpoly_trial");
        if (raw) {
          const t = JSON.parse(raw);
          if (t && t.fin && new Date(t.fin).getTime() > Date.now()) {
            setProActivo(true);
            setProOrigen(t.origen || "prueba");
            setProFin(t.fin);
            return true;
          } else {
            // Vencido: limpiar caché
            localStorage.removeItem("hitpoly_trial");
          }
        }
      } catch (e) {
      }
      return false;
    };

    const uid = authUser?.id || authUser?.user_id;
    if (!uid) {
      applyLocal();
      return;
    }
    fetch(TRIALS_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ accion: "estado", usuario_id: uid }),
    })
      .then((r) => r.json())
      .then((d) => {
        const rawLocal = localStorage.getItem("hitpoly_trial");
        const finFuture = (d && d.fin && new Date(d.fin).getTime() > Date.now());
        const activoReal = (d && d.status === "success" && (d.activo || finFuture));
        if (activoReal) {
          setProActivo(true);
          setProOrigen(d.origen || null);
          setProFin(d.fin || null);
          setTrialVencido(false);
        } else if (d && d.status === "success" && !d.activo) {
          try {
            const raw = localStorage.getItem("hitpoly_trial");
            if (raw) {
              const t = JSON.parse(raw);
              if (t && t.fin && new Date(t.fin).getTime() <= Date.now()) {
                localStorage.removeItem("hitpoly_trial");
                setProActivo(false);
                setProOrigen(null);
                setProFin(null);
                setTrialVencido(true);
              } else {
                localStorage.removeItem("hitpoly_trial");
                setProActivo(false);
                setProOrigen(null);
                setProFin(null);
                setTrialVencido(false);
              }
            } else {
              setProActivo(false);
              setProOrigen(null);
              setProFin(null);
              setTrialVencido(!!(d && d.fin));
            }
          } catch (e) {
            localStorage.removeItem("hitpoly_trial");
            setProActivo(false);
            setProOrigen(null);
            setProFin(null);
            setTrialVencido(false);
          }
        } else {
          const result = applyLocal();
          if (!result) {
            try {
              const raw = localStorage.getItem("hitpoly_trial");
              if (raw) {
                const t = JSON.parse(raw);
                if (t && t.fin && new Date(t.fin).getTime() <= Date.now()) {
                  localStorage.removeItem("hitpoly_trial");
                  setProActivo(false);
                  setProOrigen(null);
                  setProFin(null);
                  setTrialVencido(true);
                } else {
                  localStorage.removeItem("hitpoly_trial");
                  setProActivo(false);
                  setProOrigen(null);
                  setProFin(null);
                  setTrialVencido(false);
                }
              } else {
                setProActivo(false);
                setProOrigen(null);
                setProFin(null);
                setTrialVencido(false);
              }
            } catch (e) {
              setProActivo(false);
              setProOrigen(null);
              setProFin(null);
              setTrialVencido(false);
            }
          }
        }
      })
      .catch((e) => {
        applyLocal();
      });
  }, [authUser]);

  // Escuchar evento de trial activado desde otros componentes (ej. tecnologías)
  useEffect(() => {
    const handleProTrialStarted = () => {
      try {
        const raw = localStorage.getItem("hitpoly_trial");
        if (raw) {
          const t = JSON.parse(raw);
          if (t && t.fin && new Date(t.fin).getTime() > Date.now()) {
            setProActivo(true);
            setProOrigen(t.origen || "prueba");
            setProFin(t.fin);
          } else {
            localStorage.removeItem("hitpoly_trial");
          }
        }
      } catch (e) {}
    };
    window.addEventListener("pro-trial-started", handleProTrialStarted);
    return () => window.removeEventListener("pro-trial-started", handleProTrialStarted);
  }, []);

  useEffect(() => {
    const handleResize = () => setInternalIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = isMobileProp ?? internalIsMobile;
  const isCollapsed = isMobile ? false : (isHovered ? false : isManualCollapsed);
  const currentWidth = isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  const hasProfOrEnterp = useMemo(() => {
    try {
      const cached = localStorage.getItem('cached_profiles');
      if (cached) {
        const profiles = JSON.parse(cached);
        return profiles.some(p => p.type === 'professional' || p.type === 'enterprise');
      }
    } catch {}
    return false;
  }, [activeProfile]);

  // Callback estable para navegar — se pasa a todos los NavItems
  const handleNavigate = useCallback((route) => {
    navigate(route);
    if (onClose) onClose();
  }, [navigate, onClose]);

  return (
    <>
      <div
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
        style={{
        backgroundColor: COLORS.white,
        height: isMobile ? "100vh" : `calc(100vh - ${TOP_OFFSET}px)`, 
        width: isMobile ? "100%" : `${currentWidth}px`,
        display: "flex",
        flexDirection: "column",
        borderRight: isMobile ? "none" : "1px solid #f0f0f0",
        position: isMobile ? "relative" : "fixed", 
        left: 0,
        top: isMobile ? 0 : TOP_OFFSET,
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        zIndex: 10000,
      }}>
        
        {/* CABECERA: BOTÓN CREAR - Solo para perfiles profesional y empresa */}
        {(activeProfile?.type === 'professional' || activeProfile?.type === 'enterprise') && (
          <div style={{ padding: "20px 0 10px", flexShrink: 0 }}>
            <div
              onMouseEnter={() => setIsCreateHovered(true)}
              onMouseLeave={() => setIsCreateHovered(false)}
              onClick={() => { setIsCreateModalOpen(true); if(onClose) onClose(); }}
              style={{
                backgroundColor: isCreateHovered ? COLORS.fucsiaBajo : COLORS.purple,
                color: COLORS.white,
                height: isCollapsed ? "50px" : "45px",
                width: isCollapsed ? "50px" : "auto",
                margin: isCollapsed ? "0 auto" : "0 12px",
                borderRadius: "12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                paddingLeft: isCollapsed ? "0" : "16px",
                transition: "0.3s ease",
                boxShadow: isCreateHovered ? "0 4px 12px rgba(108, 77, 226, 0.3)" : "none",
              }}
            >
              <IoAdd size={24} />
              {!isCollapsed && <span style={{ marginLeft: "8px", fontSize: "14px", fontWeight: "bold", whiteSpace: "nowrap" }}>CREAR NUEVO</span>}
            </div>
          </div>
        )}

        {/* CUERPO SCROLLABLE */}
        <div className="sidebar-scroll" style={{ flex: 1, overflowY: "auto", overflowX: "hidden", paddingBottom: "20px" }}>
          <style>{`.sidebar-scroll::-webkit-scrollbar { display: none; } .sidebar-scroll { scrollbar-width: none; -ms-overflow-style: none; }`}</style>
          
          {/* SECCIÓN PRINCIPAL */}
          <NavItem name="home" icon={<IoHomeOutline/>} route="/dashboardsistem" isCollapsed={isCollapsed} currentPathname={location.pathname} onNavigate={handleNavigate} />
          
          <NavItem name="comunidad" icon={<IoSparklesOutline/>} route="/systems/comunidad?reset=true" isCollapsed={isCollapsed} currentPathname={location.pathname} onNavigate={handleNavigate} />
          <NavItem name="blog" icon={<IoNewspaperOutline/>} route="/systems/blog?reset=true" isCollapsed={isCollapsed} currentPathname={location.pathname} onNavigate={handleNavigate} />
          <NavItem name="academia" icon={<IoSchoolOutline/>} route="/systems/academia?reset=true" isCollapsed={isCollapsed} currentPathname={location.pathname} onNavigate={handleNavigate} />
          <NavItem name="crm" icon={<IoBriefcaseOutline/>} route="/systems/crm?reset=true" isCollapsed={isCollapsed} currentPathname={location.pathname} onNavigate={handleNavigate} isPro profileReady={profileReady} onProClick={openProInfoModal} proActivo={proActivo} proOrigen={proOrigen} proFin={proFin} />
          <NavItem name={activeProfile?.type === 'professional' ? 'buscar vacante' : 'bolsa de trabajo'} icon={<IoPeopleOutline/>} route="/systems/bolsa?reset=true" isCollapsed={isCollapsed} currentPathname={location.pathname} onNavigate={handleNavigate} isPro profileReady={profileReady} onProClick={openProInfoModal} proActivo={proActivo} proOrigen={proOrigen} proFin={proFin} />
          <NavItem name="proyectos" icon={<MdOutlineAssignment/>} route="/systems/proyectos?reset=true" isCollapsed={isCollapsed} currentPathname={location.pathname} onNavigate={handleNavigate} />
          <NavItem name="mensajes" icon={<IoChatbubbleEllipsesOutline/>} route="/systems/mensajes?reset=true" isCollapsed={isCollapsed} currentPathname={location.pathname} onNavigate={handleNavigate} />
        </div>

        {/* TOGGLE PLAN PRO */}
        {isCollapsed ? (
          <Tooltip
            title={proActivo ? "PRO ACTIVO" : "PRO INACTIVO"}
            placement="right"
            arrow
          >
            <div
              onClick={(e) => {
                if (e.shiftKey) {
                  localStorage.removeItem("hitpoly_trial");
                  setProActivo(false);
                  setProOrigen(null);
                  setProFin(null);
                  alert("Estado Pro limpiado manualmente. Recarga la página para ver reflejado.");
                } else {
                  if (!proActivo) {
                    openProInfoModal({ name: "Hitpoly Pro", route: "/systems/comunidad?reset=true" });
                  }
                }
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 8px",
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 20,
                  borderRadius: 10,
                  backgroundColor: proActivo ? "#16a34a" : "#F21C63",
                  position: "relative",
                  transition: "background-color 0.2s ease",
                  boxShadow: `0 0 8px ${proActivo ? "rgba(22,163,74,0.4)" : "rgba(242,28,99,0.4)"}`,
                  border: "2px solid #fff",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 2,
                    left: proActivo ? 16 : 2,
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    transition: "left 0.2s ease",
                  }}
                />
              </div>
            </div>
          </Tooltip>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              padding: "12px 16px",
              margin: "0 12px 8px 12px",
              borderRadius: 12,
              backgroundColor: proActivo ? "rgba(22,163,74,0.1)" : "rgba(242,28,99,0.1)",
              border: `2px solid ${proActivo ? "#16a34a" : "#F21C63"}`,
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onClick={(e) => {
              if (e.shiftKey) {
                localStorage.removeItem("hitpoly_trial");
                setProActivo(false);
                setProOrigen(null);
                setProFin(null);
                alert("Estado Pro limpiado manualmente. Recarga la página para ver reflejado.");
              } else {
                if (!proActivo) {
                  openProInfoModal({ name: "Hitpoly Pro", route: "/systems/comunidad?reset=true" });
                }
              }
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              {proActivo ? (
                <IoSparklesOutline size={22} color="#16a34a" style={{ flexShrink: 0 }} />
              ) : null}
              <span style={{ fontSize: 10, fontWeight: 700, color: proActivo ? "#16a34a" : "#F21C63", letterSpacing: 0.5 }}>
                {proActivo ? "PRO ACTIVO" : "PRO INACTIVO"}
              </span>
              <div
              style={{
                width: 36,
                height: 20,
                borderRadius: 10,
                backgroundColor: proActivo ? "#16a34a" : "#F21C63",
                position: "relative",
                transition: "background-color 0.2s ease",
                boxShadow: `0 0 8px ${proActivo ? "rgba(22,163,74,0.4)" : "rgba(242,28,99,0.4)"}`,
                border: "2px solid #fff",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 2,
                  left: proActivo ? 16 : 2,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  transition: "left 0.2s ease",
                }}
              />
            </div>
          </div>
        </div>
        )}

        {/* PIE: LOGOUT + TOGGLE ABAJO */}
        <div style={{ 
          padding: "10px 0", 
          borderTop: "1px solid #f0f0f0", 
          backgroundColor: COLORS.white, 
          flexShrink: 0,
          display: "flex",
          flexDirection: isCollapsed ? "column" : "row",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "space-between"
        }}>
          <div
            onMouseEnter={() => setIsLogoutHovered(true)}
            onMouseLeave={() => setIsLogoutHovered(false)}
            onClick={() => setIsLogoutModalOpen(true)}
            style={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              height: "48px",
              padding: isCollapsed ? "0" : "0 20px",
              cursor: "pointer",
              justifyContent: isCollapsed ? "center" : "flex-start",
              backgroundColor: isLogoutHovered ? "#fff0f3" : "transparent",
              color: isLogoutHovered ? COLORS.fucsia : "#666",
              transition: "0.2s ease",
            }}
          >
            <IoLogOutOutline size={24} style={{ marginRight: isCollapsed ? "0" : "12px" }} />
            {!isCollapsed && <span style={{ fontWeight: 600, fontSize: "13px", whiteSpace: "nowrap" }}>CERRAR SESIÓN</span>}
          </div>

          {!isMobile && (
            <div 
              onClick={() => setIsManualCollapsed(!isManualCollapsed)}
              style={{
                width: isCollapsed ? "100%" : "40px",
                height: "48px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "#999",
                borderLeft: isCollapsed ? "none" : "1px solid #f0f0f0",
                transition: "0.3s"
              }}
            >
              {isManualCollapsed ? <IoChevronForwardOutline size={18}/> : <IoChevronBackOutline size={18}/>}
            </div>
          )}
        </div>
      </div>

      {!isMobile && (
        <div style={{ 
          width: `${currentWidth}px`, 
          minWidth: `${currentWidth}px`, 
          transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          height: "100vh" 
        }} />
      )}

      <LogoutModal open={isLogoutModalOpen} handleClose={() => setIsLogoutModalOpen(false)} />

      <CreateNewModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        profileType={activeProfile?.type}
      />

      {/* Popup: elegir con qué perfil entrar (profesional / empresa) */}
      <Dialog
        open={profileChooserOpen}
        onClose={() => setProfileChooserOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 480, width: "90%" } }}
        sx={{ zIndex: 30000 }}
      >
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            ¿Con qué perfil quieres entrar?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {chooserTool
              ? `Tienes más de un perfil disponible. Selecciona con cuál quieres acceder a ${chooserTool}.`
              : "Tienes más de un perfil disponible. Selecciona con cuál quieres continuar."}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {chooserProfiles.map((p) => (
              <Button
                key={`${p.type}-${p.id}`}
                variant="outlined"
                fullWidth
                onClick={() => seleccionarPerfilDeEntrada(p)}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  justifyContent: "flex-start",
                  textTransform: "none",
                  gap: 1.5,
                }}
                startIcon={
                  p.avatar ? (
                    <Avatar src={p.avatar} sx={{ width: 28, height: 28 }} />
                  ) : (
                    <Avatar sx={{ width: 28, height: 28, bgcolor: COLORS.purple }}>
                      {p.type === "enterprise" ? "E" : "P"}
                    </Avatar>
                  )
                }
              >
                <Box sx={{ textAlign: "left" }}>
                  <Typography fontWeight={700} color="text.primary">
                    {p.name || (p.type === "enterprise" ? "Perfil Empresa" : "Perfil Profesional")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.label || (p.type === "enterprise" ? "Empresa" : "Profesional")}
                  </Typography>
                </Box>
              </Button>
            ))}
          </Box>
        </Box>
      </Dialog>

      {/* Gate de creación de perfil para sistemas Pro (crm / bolsa) */}
      <Dialog
        open={isCreateProfileDialogOpen}
        onClose={() => setIsCreateProfileDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 480, width: "90%" } }}
        sx={{ zIndex: 30000 }}
      >
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Crea un perfil para continuar
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {proTool
              ? `Para acceder a ${proTool} necesitas un perfil profesional o de empresa.`
              : "Necesitas un perfil profesional o de empresa para acceder a esta sección."}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => iniciarCreacionPerfil("professional")}
              sx={{ borderRadius: 2, py: 1.5, bgcolor: COLORS.fucsia, "&:hover": { bgcolor: COLORS.purple } }}
            >
              Crear perfil profesional
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => iniciarCreacionPerfil("enterprise")}
              sx={{ borderRadius: 2, py: 1.5 }}
            >
              Crear perfil empresa
            </Button>
          </Box>
        </Box>
      </Dialog>

      <ProPlanModal
        open={isProModalOpen}
        vencido={trialVencido}
        onClose={() => { setIsProModalOpen(false); setShowSubscriptionSuccess(false); }}
        herramienta={proTool}
        onTrialStarted={(fin) => {
          setProActivo(true);
          setProOrigen("prueba");
          setProFin(fin || null);
          setIsProModalOpen(false);
          setShowSubscriptionSuccess(true);
        }}
      />

      {/* Mensaje de suscripción exitosa con botón para ir al proyecto */}
      <Dialog
        open={showSubscriptionSuccess}
        onClose={() => setShowSubscriptionSuccess(false)}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 480, width: "90%" } }}
        sx={{ zIndex: 30000 }}
      >
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            ¡Te acabas de suscribir!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Tu prueba gratuita de 3 días está activa. Ahora puedes acceder a todos los beneficios de Hitpoly Pro.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              if (proRoute) navigate(proRoute);
              setShowSubscriptionSuccess(false);
            }}
            sx={{ borderRadius: 2, py: 1.5, bgcolor: COLORS.fucsia, "&:hover": { bgcolor: COLORS.purple } }}
          >
            Ir al proyecto que presioné ({proTool || "ver"})
          </Button>
        </Box>
      </Dialog>


    </>
  );
};

export default CollapsibleSidebar;
